import _ from 'lodash';
import { apolloQueryWrapper } from '@/shared/api';
import HOLTER_RHYTHM_EVENTS_COUNT_QUERY from '@/entities/event/api/queries/holterRhythmEventsCount';
import type { ReviewedCount } from '@/shared/api';

export type HolterRhythmEventsCount = {
  isSuccess: boolean;
  message: string;
  afib: ReviewedCount;
  tachy: ReviewedCount;
  brady: ReviewedCount;
  pause: ReviewedCount;
  maxHr: ReviewedCount;
  minHr: ReviewedCount;
  vt: ReviewedCount;
  svt: ReviewedCount;
  avb2: ReviewedCount;
  avb3: ReviewedCount;
};

// TODO(ts): typed as the HolterEventsFilter GraphQL input once its server-side shape is documented
/**
 * Loads the per-type Holter rhythm event counts and review progress for a study. Runs the holterRhythmEventsCount query through apolloQueryWrapper and, on success, clones the payload, strips isSuccess and message, and rewrites null buckets as a count of zero at one hundred percent reviewed, otherwise throwing the server message.
 *
 * @param filter - GraphQL filter narrowing the counts to a study and time range.
 * @param isUsingCache - When true, lets the wrapper serve a cached result.
 * @param key - Cancellation key identifying this request.
 * @param needCancel - Whether an in-flight request under the same key is cancelled first.
 * @returns A promise resolving to a map of rhythm event type to its count and reviewed percentage.
 */
const fetchHolterRhythmEventsCount = async (
  filter: any,
  isUsingCache?: boolean,
  key?: string | null,
  needCancel = true,
) => {
  const result = await apolloQueryWrapper<{ holterRhythmEventsCount: HolterRhythmEventsCount }>({
    options: {
      query: HOLTER_RHYTHM_EVENTS_COUNT_QUERY,
      variables: {
        filter,
      },
    },
    isUsingCache,
    key,
    needCancel,
  });
  const { data } = result;
  const { holterRhythmEventsCount } = data;
  if (holterRhythmEventsCount?.isSuccess) {
    // cloneDeep to mutate read only object
    const objectClone: Record<string, any> = _.cloneDeep(holterRhythmEventsCount);
    delete objectClone.isSuccess;
    delete objectClone.message;
    _.forEach(objectClone, (value, key) => {
      if (value === null) {
        objectClone[key] = {
          count: 0,
          reviewed: 100,
        };
      }
    });
    return objectClone;
  }
  throw new Error(holterRhythmEventsCount.message);
};

export default fetchHolterRhythmEventsCount;
